import {
  Alert,
  AlertIcon,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  Text
} from '@chakra-ui/react'

import { FormField } from '../../../../../components/FormField'
import { Icon } from '../../../../../components/Icon'
import { SearchableSelect } from '../../../../../components/Select'
import { Tooltip } from '../../../../../components/Tooltip'
import { slugify, WEBSITE_URL } from '../../../../../helpers'
import { ISelectCourse } from '../../../../../services/CoursesService/interfaces'
import { EventComplete, EventType } from '../../../../../services/Event/types'
import { automaticSlug } from '../editionNumbering'
import { GeneralFieldsEditor } from '../GeneralFieldsEditor'
import { EditionForm, SeriesForm } from './eventSeriesFormShared'

interface IProps {
  form: SeriesForm
  patchSeries: (patch: Partial<SeriesForm>) => void
  fieldErrors: Record<string, string[]>
  saving: boolean
  sourceEvent?: EventComplete
  eventTypes: EventType[]
  courses: ISelectCourse[]
  activeCampaignLists: { id: string; name: string }[]
  editionsChangingSlug: EditionForm[]
}

export function GeneralTab({
  form,
  patchSeries,
  fieldErrors,
  saving,
  sourceEvent,
  eventTypes,
  courses,
  activeCampaignLists,
  editionsChangingSlug
}: IProps) {
  return (
    <>
      {sourceEvent && (
        <Alert status="info" fontSize="sm" borderRadius="md" mb={5}>
          <AlertIcon />O evento “{sourceEvent.name}” será adicionado como primeira edição desta nova série, mantendo
          seus ingressos, inscritos e demais configurações. Os campos abaixo definem os padrões da série para as
          próximas edições.
        </Alert>
      )}

      <Text variant="caption" mb={4}>
        Estas configurações são aplicadas a todas as edições. Cada edição pode sobrescrever qualquer uma delas.
      </Text>

      <FormField label="Nome da Série" error={fieldErrors['name']?.[0]} isRequired mb={5}>
        <Input value={form.name} onChange={(e) => patchSeries({ name: e.target.value })} disabled={saving} />
      </FormField>

      <FormField
        label="Cursos relacionados"
        error={fieldErrors['course_ids']}
        helperText={
          <>
            O evento aparecerá no carrossel &quot;Gostou desse curso?&quot; das páginas dos cursos selecionados. Vale
            para <b>todas as edições</b> — não há como escolher cursos por edição.
          </>
        }
        mb={5}
      >
        <SearchableSelect<number>
          isMulti
          options={courses.map((course) => ({ value: course.id, label: course.name }))}
          value={form.course_ids}
          onChange={(courseIds) => patchSeries({ course_ids: courseIds })}
          placeholder="Selecione os cursos"
          entity="curso"
          isDisabled={saving}
        />
      </FormField>

      <FormControl isInvalid={!!fieldErrors['slug']} mb={5}>
        <FormLabel display="flex" alignItems="center" gap={1.5}>
          URL da Série *
          <Tooltip label="Cada edição recebe a URL automaticamente: a 1ª fica com a URL da série e as seguintes ganham o sufixo da edição (ex.: /evento-artes-2edicao). O nº segue a ordem da data de início. Para dar uma URL própria a uma edição específica, ative a tag “URL” nela.">
            <Icon name="lni lni-information" size="sm" color="gray.400" cursor="help" />
          </Tooltip>
        </FormLabel>
        <InputGroup>
          <InputLeftAddon>{`${WEBSITE_URL}/eventos/`}</InputLeftAddon>
          <Input
            value={form.slug}
            onChange={(e) => patchSeries({ slug: slugify(e.target.value) })}
            disabled={saving}
            maxLength={255}
            placeholder="minha-serie"
          />
        </InputGroup>
        {sourceEvent && (
          <FormHelperText>
            “{sourceEvent.name}” mantém a URL atual (/{sourceEvent.slug}). Esta URL base é a das próximas edições (ex.:
            /{automaticSlug(form.slug || 'minha-serie', 2)}).
          </FormHelperText>
        )}
        {/* Changing the base URL re-derives every automatic edition at once, and the
        cards that warn about it are far below (the modal scrolls, Salvar does not) —
        so warn where the change is actually made. */}
        {editionsChangingSlug.length > 0 && (
          <Alert status="warning" fontSize="sm" borderRadius="md" mt={2}>
            <AlertIcon />
            <Text>
              <b>{editionsChangingSlug.length}</b> {editionsChangingSlug.length === 1 ? 'edição terá' : 'edições terão'}{' '}
              a URL pública alterada ao salvar. Links já divulgados deixarão de funcionar.
            </Text>
          </Alert>
        )}
        <FormErrorMessage>{fieldErrors['slug']?.[0]}</FormErrorMessage>
      </FormControl>

      <GeneralFieldsEditor
        value={form}
        onChange={patchSeries}
        groups={['format', 'types', 'thumb', 'thumb_wide', 'video_url']}
        eventTypes={eventTypes}
        activeCampaignLists={activeCampaignLists}
        formatForLocation={form.format}
        disabled={saving}
        errors={fieldErrors}
      />
    </>
  )
}
